/**
 * Compute dynamic CSS custom property tokens for the preview root.
 * Mirrors the logic in generators/css/tokens.ts so the preview
 * reflects borderRadius, buttonStyle, and color changes in real-time.
 */
import { adjustColorBrightness, getRelativeLuminance } from '@/utils/color';
import { eliteStylePresets } from '@/data/elite-presets';

function hexToRgb(hex: string): [number, number, number] {
  hex = hex.replace(/^#/, '');
  if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  const n = parseInt(hex, 16);
  if (Number.isNaN(n)) return [0, 0, 0];
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbString(hex: string): string {
  return hexToRgb(hex).join(' ');
}

export function computePreviewTokens(settings: any): Record<string, string> {
  const primaryColor = settings.primaryColor || '#134e4a';
  const accentColor = settings.accentColor || '#d97706';
  const primaryLight = adjustColorBrightness(primaryColor, 20);
  const primaryDark = adjustColorBrightness(primaryColor, -20);
  const onPrimary = getRelativeLuminance(primaryColor) > 0.179 ? '#1a1a1a' : '#ffffff';

  // --- Colors ---
  const tokens: Record<string, string> = {
    '--c-primary': primaryColor,
    '--c-primary-light': primaryLight,
    '--c-primary-dark': primaryDark,
    '--c-accent': accentColor,
    '--c-accent-light': adjustColorBrightness(accentColor, 20),
    '--c-on-primary': onPrimary,
    '--c-primary-rgb': rgbString(primaryColor),
    '--c-accent-rgb': rgbString(accentColor),
  };

  // --- Border Radius ---
  const borderRadius = settings.borderRadius || 'rounded';
  const preset = eliteStylePresets[settings.stylePreset || 'signature'];
  const presetShape = preset?.shape || {
    radiusSm: '4px',
    radiusMd: '10px',
    radiusLg: '14px',
    radiusXl: '18px',
  };
  const shape = borderRadius === 'none'
    ? { radiusSm: '0px', radiusMd: '0px', radiusLg: '0px', radiusXl: '0px' }
    : borderRadius === 'pill'
      ? { radiusSm: '9999px', radiusMd: '9999px', radiusLg: '9999px', radiusXl: '9999px' }
      : presetShape;

  tokens['--radius-sm'] = shape.radiusSm;
  tokens['--radius-md'] = shape.radiusMd;
  tokens['--radius-lg'] = shape.radiusLg;
  tokens['--radius-xl'] = shape.radiusXl;

  // --- Button Radius (separate from element radius) ---
  const buttonRadius = settings.buttonRadius ?? settings.borderRadius ?? 'rounded';
  const btnShape = buttonRadius === 'none'
    ? { radiusSm: '0px', radiusMd: '0px', radiusLg: '0px', radiusXl: '0px' }
    : buttonRadius === 'pill'
      ? { radiusSm: '9999px', radiusMd: '9999px', radiusLg: '9999px', radiusXl: '9999px' }
      : presetShape;
  tokens['--btn-radius'] = btnShape.radiusMd;

  // --- Button Style ---
  const buttonStyle = settings.buttonStyle || 'filled';
  switch (buttonStyle) {
    case 'outline':
      tokens['--btn-primary-bg'] = 'transparent';
      tokens['--btn-primary-color'] = primaryColor;
      tokens['--btn-primary-border'] = primaryColor;
      tokens['--btn-primary-hover-bg'] = `rgba(${rgbString(primaryColor)} / 0.08)`;
      tokens['--btn-primary-hover-border'] = primaryLight;
      tokens['--btn-primary-hover-color'] = primaryLight;
      break;
    case 'soft':
      tokens['--btn-primary-bg'] = `rgba(${rgbString(primaryColor)} / 0.1)`;
      tokens['--btn-primary-color'] = primaryColor;
      tokens['--btn-primary-border'] = 'transparent';
      tokens['--btn-primary-hover-bg'] = `rgba(${rgbString(primaryColor)} / 0.18)`;
      tokens['--btn-primary-hover-border'] = 'transparent';
      tokens['--btn-primary-hover-color'] = primaryDark;
      break;
    default: // 'filled'
      tokens['--btn-primary-bg'] = primaryColor;
      tokens['--btn-primary-color'] = onPrimary;
      tokens['--btn-primary-border'] = primaryColor;
      tokens['--btn-primary-hover-bg'] = primaryLight;
      tokens['--btn-primary-hover-border'] = primaryLight;
      tokens['--btn-primary-hover-color'] = onPrimary;
      break;
  }

  return tokens;
}
